import IconPlay from "app/icons/IconPlay";
import IconStop from "app/icons/IconStop";
import MenuButton from "app/ui/MenuButton";
import cn from "app/utils/classnames";
import usePreloadedAudio, {
  type PreloadedAudio,
} from "app/utils/usePreloadedAudio";
import useWindowSize, { WindowSize } from "app/utils/useWindowSize";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export interface PlayAudioButtonHandle {
  forcePlay: (audio?: PreloadedAudio | undefined) => void;
  forceStop: () => void;
}

interface PlayAudioButtonProps {
  audioUrl: string;
  onClick: () => void;
  onAudioEnd?: () => void;
  children: React.ReactNode;
  className?: string;
  iconOverride?: React.ReactNode;
  iconSize?: number;
}

const PlayAudioButton = forwardRef<PlayAudioButtonHandle, PlayAudioButtonProps>(
  (
    {
      audioUrl,
      onClick,
      children,
      className = "",
      iconOverride = undefined,
      iconSize = 24,
    }: PlayAudioButtonProps,
    ref,
  ) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const defaultAudio = usePreloadedAudio();
    const forcedAudio = useRef<PreloadedAudio | null>(null);

    useEffect(() => {
      defaultAudio.preload(audioUrl);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioUrl]);

    useImperativeHandle(ref, () => ({
      forcePlay(audio) {
        defaultAudio.stop();
        forcedAudio.current?.stop();

        if (audio) {
          forcedAudio.current = audio;
          forcedAudio.current?.play(() => setIsPlaying(false));
        } else {
          defaultAudio.play(() => setIsPlaying(false));
        }

        setIsPlaying(true);
      },
      forceStop() {
        forcedAudio.current?.stop();
        defaultAudio.stop();
        setIsPlaying(false);
      },
    }));

    const { windowWidth } = useWindowSize();

    function handleClick() {
      if (isPlaying) {
        forcedAudio.current?.stop();
        defaultAudio.stop();
        setIsPlaying(false);
      } else {
        setIsPlaying(true);
        defaultAudio.play(() => setIsPlaying(false));
      }

      onClick();
    }

    const icon = iconOverride ? (
      iconOverride
    ) : isPlaying ? (
      <IconStop width={iconSize} height={iconSize} />
    ) : (
      <IconPlay width={iconSize} height={iconSize} />
    );

    return (
      <MenuButton
        className={cn("text-xs lg:text-sm", className)}
        onClick={handleClick}
        icon={icon}
        elevation={windowWidth === WindowSize.LARGE ? "lg" : "md"}
        forcePressed={isPlaying}
      >
        {children}
      </MenuButton>
    );
  },
);

export default PlayAudioButton;
